import React, { useState, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Plus, Loader2, AlertTriangle } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryState, useQueryStates, parseAsInteger, parseAsString, parseAsArrayOf, createParser } from 'nuqs'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { PurchaseInvoiceDataTable } from './PurchaseInvoiceDataTable'
import {
  getColumns,
  editPurchaseInvoiceEvent,
  deletePurchaseInvoiceEvent,
  markAsPaidPurchaseInvoiceEvent
} from './columns'
import { listVouchers, deleteVoucher } from '@/app/services/voucherService'
import { listSites } from '@/app/services/siteService'
import { VoucherResponse } from '@/app/types/voucher'
import { Site } from '@/app/types/site'
import { useOrganization } from '@/app/hooks/useOrganization'
import { PaginatedResponse, ListQueryParams } from '@/app/types/api'
import { SortingState } from '@tanstack/react-table'
import { getBackendStatusFilters, SimplifiedSalesInvoiceStatus } from '@/app/lib/statusUtils'
import { QuickPaymentOutModal } from '../components/QuickPaymentOutModal'
import { usePreferenceStore } from '@/app/store/preferenceStore'

// Extended PaginatedResponse with page count (backend might return different field names)
interface ExtendedPaginatedResponse<T> extends PaginatedResponse<T> {
  page_count?: number
  pageCount?: number
  total_pages?: number
  totalPages?: number
}

// Define the sort direction type
type SortDirection = 'asc' | 'desc'

// Custom parser for sorting state
const sortingParser = createParser({
  parse: (value: string) => {
    if (!value) return [{ id: 'created_at', desc: true }]
    const [field, direction] = value.split('-')
    return [{
      id: field || 'created_at',
      desc: direction === 'desc'
    }]
  },
  serialize: (value: SortingState) => {
    if (!value.length) return 'created_at-desc'
    const sort = value[0]
    return `${sort.id}-${sort.desc ? 'desc' : 'asc'}`
  }
})

// Custom parser for page (0-based internal, 1-based in URL)
const pageParser = createParser({
  parse: (value: string) => {
    const page = parseInt(value, 10)
    return isNaN(page) || page < 1 ? 0 : page - 1 // Convert 1-based to 0-based
  },
  serialize: (value: number) => {
    return String(value + 1) // Convert 0-based to 1-based
  }
})

/**
 * Filter Configuration
 * -------------------
 * This configuration controls how filters are built for the API.
 *
 * To change the filter format globally, update FILTER_CONFIG.format:
 * - 'array': Uses array of objects with field, value, op (best for advanced filtering)
 *   Example: [{"field":"party.name","value":"%han%","op":"ilike"}]
 *
 * - 'object': Uses simple key-value pairs (best for exact matches)
 *   Example: {"party.name":"haniffa","status":"Paid"}
 *
 * To add special handling for a field:
 * 1. Add an entry to FILTER_CONFIG.fields with the field name
 * 2. Configure options like:
 *    - op: The operator to use (eq, like, ilike, gt, lt, etc.)
 *    - field: Map to a different backend field name
 *    - transform: Function to transform the value (e.g., add wildcards)
 *    - specialCase: Function for custom handling based on value
 */
// Filter configuration object for consistent filtering approach
const FILTER_CONFIG = {
  // Filter format to use: 'array' (with field/value/op objects) or 'object' (simple key-value pairs)
  format: 'array' as const,

  // Special field configurations
  fields: {
    'voucher_number': {
      op: 'ilike',
      transform: (value: string): string => `%${value}%` // Wrap with wildcards for case-insensitive contains search
    },
    'party.name': {
      op: 'ilike',
      transform: (value: string): string => `%${value}%` // Wrap with wildcards for contains search
    },
    'reference_number': {
      op: 'ilike',
      transform: (value: string): string => `%${value}%` // Wrap with wildcards for case-insensitive contains search
    },
    status: {
      // Special mapping for status filters
      specialCase: (
        value: string
      ):
        | { field: string; value: string | number | boolean | string[]; op: string }
        | { field: string; value: string | number | boolean | string[]; op: string }[]
        | null => {
        // Use the reverse mapping function to get backend filters
        const backendFilters = getBackendStatusFilters(value as SimplifiedSalesInvoiceStatus)
        return backendFilters.length > 0 ? backendFilters : null
      }
    },
    'site_id': {
      op: 'in' // Use 'in' operator for multi-select
    },
    'total_amount': {
      op: 'eq',
      transform: (value: string | number): number => {
        const numValue = Number(value);
        return isNaN(numValue) ? 0 : numValue;
      }
    }
  }
}

// Define filter object types
interface FilterObject {
  field: string
  value: string | number | boolean | string[]
  op: string
}

// Helper function to build filter parameters based on config
const buildFilterParams = (
  filters: Record<string, string | number | boolean | string[] | { from?: string; to?: string }>,
  config = FILTER_CONFIG
): string => {
  console.log('buildFilterParams called with filters:', filters)

  if (config.format === 'array') {
    // Build array of filter objects with field, value, op
    const filterArray: (FilterObject | FilterObject[] | null)[] = Object.entries(filters).map(
      ([field, value]) => {
        // Skip empty values
        if (value === undefined || value === null || value === '') {
          return null
        }

        // Handle date range filters
        if ((field === 'transaction_date' || field === 'created_at') && typeof value === 'object' && 'from' in value) {
          const dateFilter = value as { from?: string; to?: string }
          const dateFilters: FilterObject[] = []

          console.log('Processing date filter:', dateFilter)

          if (dateFilter.from) {
            // Ensure we add time component for start of day
            const fromDate = `${dateFilter.from} 00:00:00`
            dateFilters.push({
              field: field, // Use the actual field (created_at or transaction_date)
              value: fromDate,
              op: 'gte' // Greater than or equal
            })
          }

          if (dateFilter.to) {
            // Ensure we add time component for end of day
            const toDate = `${dateFilter.to} 23:59:59`
            dateFilters.push({
              field: field, // Use the actual field (created_at or transaction_date)
              value: toDate,
              op: 'lte' // Less than or equal
            })
          }

          console.log('Generated date filters:', dateFilters)
          return dateFilters.length > 0 ? dateFilters : null
        }

        // Handle array values (for multi-select filters)
        if (Array.isArray(value)) {
          // Skip empty arrays
          if (value.length === 0) {
            return null
          }
          
          const fieldConfig = config.fields[field as keyof typeof config.fields]
          const actualField = (fieldConfig && 'field' in fieldConfig) ? fieldConfig.field : field
          const op = (fieldConfig && 'op' in fieldConfig) ? fieldConfig.op : 'in'
          
          return {
            field: actualField,
            value: value, // Send array as-is
            op: op
          } as FilterObject
        }

        // Check for special case handling
        const fieldConfig = config.fields[field as keyof typeof config.fields]
        if (fieldConfig && 'specialCase' in fieldConfig && fieldConfig.specialCase) {
          const specialCase = fieldConfig.specialCase(String(value))
          if (specialCase) {
            // Special case can return either a single filter or an array of filters
            return specialCase as FilterObject | FilterObject[]
          }
        }

        // Get actual field name from config or use the original
        const actualField = (fieldConfig && 'field' in fieldConfig) ? fieldConfig.field : field

        // Transform value if needed
        const transformedValue = (fieldConfig && 'transform' in fieldConfig && fieldConfig.transform)
          ? fieldConfig.transform(String(value))
          : String(value)

        // Use configured operator or default to 'eq'
        const op = (fieldConfig && 'op' in fieldConfig) ? fieldConfig.op : 'eq'

        return {
          field: actualField,
          value: transformedValue,
          op // Use 'op' instead of 'operator'
        } as FilterObject
      }
    )

    // Flatten the array (to handle date range filters that return arrays)
    const flattenedArray = filterArray.flatMap((f) => f || []).filter(Boolean)

    const result = JSON.stringify(flattenedArray)
    console.log('Final filter JSON:', result)
    return result
  } else {
    // Simple object format for exact matches
    const filterObj: Record<string, string | number | boolean> = {}

    Object.entries(filters).forEach(([field, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Apply field mapping if configured
        const fieldConfig = config.fields[field as keyof typeof config.fields]
        const actualField = (fieldConfig && 'field' in fieldConfig) ? fieldConfig.field : field
        // For object format, only accept simple types
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          filterObj[actualField] = value
        }
      }
    })

    return JSON.stringify(filterObj)
  }
}

const PurchaseInvoicesPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { currentOrganizationId } = useOrganization()
  const { defaultSiteId } = usePreferenceStore()
  const navigate = useNavigate()

  // Use nuqs for URL state management
  const [page, setPage] = useQueryState('page', pageParser.withDefault(0))
  const [search, setSearch] = useQueryState('search', parseAsString.withDefault(''))
  const [sorting, setSorting] = useQueryState('sortBy', sortingParser.withDefault([{ id: 'created_at', desc: true }]))
  
  // Use nuqs for filter state management
  const [filterStates, setFilterStates] = useQueryStates({
    voucher_number: parseAsString,
    party_name: parseAsString, // Changed from 'party.name' to avoid URL encoding issues
    status: parseAsString,
    reference_number: parseAsString,
    site_id: parseAsArrayOf(parseAsInteger), // Multi-select site filter
    transaction_date_from: parseAsString,
    transaction_date_to: parseAsString,
    created_at_from: parseAsString,
    created_at_to: parseAsString,
    total_amount: parseAsString,
  }, {
    // Use shallow routing to prevent full page reloads
    shallow: true,
  })

  // Build filters object from nuqs state
  const filters: Record<string, string | number | boolean | string[] | { from?: string; to?: string }> = {}
  
  // Add simple filters
  if (filterStates.voucher_number) filters.voucher_number = filterStates.voucher_number
  if (filterStates.party_name) filters['party.name'] = filterStates.party_name // Map party_name to party.name
  if (filterStates.status) filters.status = filterStates.status
  if (filterStates.reference_number) filters.reference_number = filterStates.reference_number
  if (filterStates.site_id && filterStates.site_id.length > 0) filters.site_id = filterStates.site_id
  if (filterStates.total_amount) filters.total_amount = filterStates.total_amount
  
  // Add date range filters
  if (filterStates.transaction_date_from || filterStates.transaction_date_to) {
    filters.transaction_date = {
      from: filterStates.transaction_date_from || undefined,
      to: filterStates.transaction_date_to || undefined
    }
  }
  if (filterStates.created_at_from || filterStates.created_at_to) {
    filters.created_at = {
      from: filterStates.created_at_from || undefined,
      to: filterStates.created_at_to || undefined
    }
  }

  // State for dialogs only
  const [limit] = useState<number>(10)

  // Update document title
  useEffect(() => {
    document.title = 'Purchase Invoices'
    return () => {
      document.title = 'Billing App'
    }
  }, [])
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState<boolean>(false)
  const [selectedPurchaseInvoice, setSelectedPurchaseInvoice] = useState<VoucherResponse | null>(null)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState<boolean>(false)
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] =
    useState<VoucherResponse | null>(null)

  // Fetch Sites for filter
  const { data: sitesData } = useQuery({
    queryKey: ['sites', currentOrganizationId],
    queryFn: () => listSites({ limit: 100 }),
    enabled: !!currentOrganizationId,
  })

  // Determine sort parameters for API call
  const getSortParams = useCallback(() => {
    if (sorting.length === 0)
      return {
        sort_by: 'created_at',
        sort_dir: 'desc' as SortDirection
      }

    const sort = sorting[0]
    const direction: SortDirection = sort.desc ? 'desc' : 'asc'

    return {
      sort_by: sort.id,
      sort_dir: direction
    }
  }, [sorting])

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Record<string, unknown>): void => {
    console.log('handleFilterChange called with:', newFilters)
    
    // Build the new filter states for nuqs
    const updates: Record<string, string | string[] | null> = {}
    let hasActualChanges = false

    // List of all possible filter keys that could come from the DataTable
    const knownFilterKeys = ['voucher_number', 'party.name', 'status', 'reference_number', 'site_id', 'transaction_date', 'created_at', 'total_amount']
    
    // First, check all known keys and set to null if not present in newFilters
    knownFilterKeys.forEach(key => {
      if (!(key in newFilters)) {
        const mappedKey = key === 'party.name' ? 'party_name' : key
        
        // For date range filters, clear both from and to
        if (key === 'transaction_date' || key === 'created_at') {
          if (filterStates[`${key}_from`] !== null || filterStates[`${key}_to`] !== null) {
            hasActualChanges = true
            updates[`${key}_from`] = null
            updates[`${key}_to`] = null
          }
        } else if (filterStates[mappedKey] !== null) {
          hasActualChanges = true
          updates[mappedKey] = null
        }
      }
    })

    Object.entries(newFilters).forEach(([key, value]) => {
      // Map 'party.name' to 'party_name' for URL compatibility
      const mappedKey = key === 'party.name' ? 'party_name' : key
      
      // Handle date range object
      if (typeof value === 'object' && value !== null && 'from' in value) {
        const dateRange = value as { from?: string; to?: string }
        const fromKey = `${key}_from`
        const toKey = `${key}_to`
        const newFrom = dateRange.from || null
        const newTo = dateRange.to || null
        
        // Check if values actually changed
        if (filterStates[fromKey] !== newFrom || filterStates[toKey] !== newTo) {
          hasActualChanges = true
        }
        
        updates[fromKey] = newFrom
        updates[toKey] = newTo
      } else if (Array.isArray(value)) {
        // Handle arrays for multi-select filters
        const newValue = value.length > 0 ? value : null
        const currentValue = filterStates[mappedKey]
        
        // Check if array values changed
        if (JSON.stringify(currentValue) !== JSON.stringify(newValue)) {
          hasActualChanges = true
        }
        
        updates[mappedKey] = newValue
      } else if (value === undefined || value === null || value === '') {
        // Clear the filter
        if (filterStates[mappedKey] !== null) {
          hasActualChanges = true
        }
        updates[mappedKey] = null
      } else {
        // Handle simple string/number values
        const newValue = String(value)
        if (filterStates[mappedKey] !== newValue) {
          hasActualChanges = true
        }
        updates[mappedKey] = newValue
      }
    })

    console.log('Updating filters with:', updates, 'hasActualChanges:', hasActualChanges)
    console.log('Current filterStates:', filterStates)
    
    // Update filters using nuqs
    setFilterStates(updates)
    
    // Only reset to first page when filters actually change
    if (hasActualChanges) {
      setPage(0)
    }
  }, [setFilterStates, setPage, filterStates])

  // Fetch purchase invoices with search, pagination, sorting and filters
  const {
    data: purchaseInvoicesData,
    isLoading,
    isError,
    error
  } = useQuery<ExtendedPaginatedResponse<VoucherResponse>>({
    queryKey: ['purchase-invoices', page, limit, search, sorting, filters, currentOrganizationId, defaultSiteId],
    queryFn: () => {
      const { sort_by, sort_dir } = getSortParams()
      const skip = page * limit // Calculate skip based on page and limit

      // Create params object with basic query parameters
      const params: ListQueryParams = {
        skip,
        limit,
        sort_by,
        sort_dir,
        voucher_type_id: 2 // Purchase Invoice voucher type ID
      }

      // Add search if provided
      if (search) {
        params.search_query = search
        // Also add search_fields to search across multiple fields
        params.search_fields = JSON.stringify(['voucher_number', 'party.name', 'reference_number'])
      }

      // Handle filters
      const activeFilters = { ...filters }
      
      if (Object.keys(activeFilters).length > 0) {
        console.log('Current filters state:', activeFilters)
        // Build filter parameters based on configuration
        params.filters = buildFilterParams(activeFilters)
        console.log('Filter JSON:', params.filters)
      }

      console.log('API Query Params:', params)
      return listVouchers(params)
    },
    enabled: !!currentOrganizationId
  })

  // Calculate page count from total items and limit if not directly provided
  const pageCount =
    purchaseInvoicesData?.page_count ??
    purchaseInvoicesData?.pageCount ??
    purchaseInvoicesData?.total_pages ??
    purchaseInvoicesData?.totalPages ??
    (purchaseInvoicesData ? Math.ceil(purchaseInvoicesData.total / limit) : 0)

  // Delete purchase invoice mutation
  const deletePurchaseInvoiceMutation = useMutation({
    mutationFn: (id: number) => deleteVoucher(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-invoices'] })
      setIsDeleteAlertOpen(false)
      setSelectedPurchaseInvoice(null)
      toast.success('Purchase invoice deleted successfully')
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete purchase invoice: ${error.message}`)
    }
  })

  // Event handlers for table actions
  useEffect(() => {
    const handleEditPurchaseInvoice = (
      event: CustomEvent<{ purchaseInvoice: VoucherResponse }>
    ): void => {
      // Use navigate instead of window.location.href
      navigate(`/purchase-invoices/detail/${event.detail.purchaseInvoice.id}`)
    }

    const handleDeletePurchaseInvoice = (
      event: CustomEvent<{ purchaseInvoice: VoucherResponse }>
    ): void => {
      setSelectedPurchaseInvoice(event.detail.purchaseInvoice)
      setIsDeleteAlertOpen(true)
    }

    const handleMarkAsPaidPurchaseInvoice = (
      event: CustomEvent<{ purchaseInvoice: VoucherResponse }>
    ): void => {
      setSelectedInvoiceForPayment(event.detail.purchaseInvoice)
      setIsPaymentModalOpen(true)
    }

    document.addEventListener(editPurchaseInvoiceEvent, handleEditPurchaseInvoice as EventListener)
    document.addEventListener(deletePurchaseInvoiceEvent, handleDeletePurchaseInvoice as EventListener)
    document.addEventListener(
      markAsPaidPurchaseInvoiceEvent,
      handleMarkAsPaidPurchaseInvoice as EventListener
    )

    return () => {
      document.removeEventListener(editPurchaseInvoiceEvent, handleEditPurchaseInvoice as EventListener)
      document.removeEventListener(
        deletePurchaseInvoiceEvent,
        handleDeletePurchaseInvoice as EventListener
      )
      document.removeEventListener(
        markAsPaidPurchaseInvoiceEvent,
        handleMarkAsPaidPurchaseInvoice as EventListener
      )
    }
  }, [navigate])

  // Handle search input change
  const handleSearchChange = useCallback((value: string): void => {
    setSearch(value)
    setPage(0) // Reset to first page on search
  }, [])

  // Handle sorting change
  const handleSortingChange = useCallback((newSorting: SortingState): void => {
    setSorting(newSorting)
    setPage(0) // Reset to first page on sort change
  }, [])

  // Handle page change
  const handlePageChange = useCallback((newPage: number): void => {
    setPage(newPage)
  }, [])

  // Handle delete confirmation
  const handleDeletePurchaseInvoice = (): void => {
    if (selectedPurchaseInvoice) {
      deletePurchaseInvoiceMutation.mutate(selectedPurchaseInvoice.id)
    }
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Purchase Invoices</h1>
        <Button asChild>
          <Link to="/purchase-invoices/detail">
            <Plus className="h-4 w-4 mr-2" />
            New Purchase Invoice
          </Link>
        </Button>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading purchase invoices: {(error as Error).message}
          </AlertDescription>
        </Alert>
      )}

      {/* Purchase Invoices Data Table */}
      <PurchaseInvoiceDataTable
        data={purchaseInvoicesData?.items || []}
        columns={getColumns(
          sitesData?.items?.map((site: Site) => ({
            label: site.name,
            value: site.id
          })) || []
        )}
        isLoading={isLoading}
        pageCount={pageCount}
        page={page}
        setPage={handlePageChange}
        onSortingChange={handleSortingChange}
        sortingState={sorting}
        onFilterChange={handleFilterChange}
        initialFilters={filters}
      />

      {/* Delete Confirmation Alert */}
      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete purchase invoice &quot;
              {selectedPurchaseInvoice?.voucher_number}&quot;. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePurchaseInvoice}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletePurchaseInvoiceMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quick Payment Modal */}
      {selectedInvoiceForPayment && (
        <QuickPaymentOutModal
          isOpen={isPaymentModalOpen}
          onClose={() => {
            setIsPaymentModalOpen(false)
            setSelectedInvoiceForPayment(null)
          }}
          invoice={selectedInvoiceForPayment}
        />
      )}
    </div>
  )
}

export default PurchaseInvoicesPage